import React from 'react';
import {
  Dialog,
  DialogContent,
  Box,
  Typography,
  Chip,
  Avatar,
  Stepper,
  Step,
  StepLabel,
  StepConnector,
  Button,
  Divider,
} from '@mui/material';
import type { StepIconProps } from '@mui/material';
import type { CupidCardAdminDetailDto } from '../../../types';

// ─── Types ───────────────────────────────────────────────────────────────────

type DetailDisplayStatus = 'LOCKED' | 'OPENED' | 'EXPIRED_EXTENDABLE' | 'DEAD';

// ─── Utils ───────────────────────────────────────────────────────────────────

function getDetailStatus(card: CupidCardAdminDetailDto): DetailDisplayStatus {
  if (card.isDead) return 'DEAD';
  if (card.isExpiredExtendable) return 'EXPIRED_EXTENDABLE';
  if (card.status === 'OPENED') return 'OPENED';
  return 'LOCKED';
}

const STATUS_CONFIG: Record<DetailDisplayStatus, { bgcolor: string; color: string; label: string }> = {
  LOCKED:             { bgcolor: '#F3F4F6', color: '#374151', label: '미오픈' },
  OPENED:             { bgcolor: '#D1FAE5', color: '#065F46', label: '오픈됨' },
  EXPIRED_EXTENDABLE: { bgcolor: '#FEF3C7', color: '#92400E', label: '만료 (연장 가능)' },
  DEAD:               { bgcolor: '#FEE2E2', color: '#991B1B', label: '소멸' },
};

const EVENT_CODE_MAP: Record<string, { label: string; icon: string }> = {
  CONSUME_CUPID_CARD_OPEN:   { label: '카드 오픈',       icon: '♥' },
  CONSUME_CUPID_CARD_EXTEND: { label: '카드 연장',       icon: '♥♥♥' },
  REWARD_SIGNUP:             { label: '신규 가입 보상',   icon: '🎁' },
  REWARD_PROFILE_COMPLETE:   { label: '프로필 완성 보상', icon: '🎁' },
  PURCHASE_HEARTS:           { label: '하트 충전',       icon: '💳' },
};

function formatDT(isoStr: string): string {
  return new Date(isoStr).toLocaleString('ko-KR', {
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function getInitials(name: string): string {
  return name ? name.charAt(0).toUpperCase() : '?';
}

// ─── Custom Step Icon ─────────────────────────────────────────────────────────

function CustomStepIcon({ completed, active, icon }: StepIconProps) {
  const bgcolor = active ? '#F59E0B' : completed ? '#10B981' : '#D1D5DB';
  return (
    <Box
      sx={{
        width: 28,
        height: 28,
        borderRadius: '50%',
        bgcolor,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: '#fff',
        fontSize: 12,
        fontWeight: 700,
        boxShadow: active ? '0 0 0 3px #FDE68A' : 'none',
      }}
    >
      {icon}
    </Box>
  );
}

const DashedConnector = (
  <StepConnector
    sx={{
      '& .MuiStepConnector-line': {
        borderColor: '#10B981',
        borderTopWidth: 2,
      },
      '&.Mui-disabled .MuiStepConnector-line': {
        borderStyle: 'dashed',
        borderColor: '#D1D5DB',
        borderTopWidth: 2,
      },
    }}
  />
);

// ─── Section Header ───────────────────────────────────────────────────────────

const SectionHeader: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Typography
    sx={{
      color: '#6B7280',
      fontSize: 12,
      fontWeight: 600,
      textTransform: 'uppercase',
      letterSpacing: 1,
      mb: 1.5,
    }}
  >
    {children}
  </Typography>
);

// ─── Props ────────────────────────────────────────────────────────────────────

interface CupidCardDetailModalProps {
  card: CupidCardAdminDetailDto | null;
  onClose: () => void;
  onForceExpire: (cardId: number) => void;
}

// ─── Component ────────────────────────────────────────────────────────────────

const CupidCardDetailModal: React.FC<CupidCardDetailModalProps> = ({ card, onClose, onForceExpire }) => {
  if (!card) return null;

  const status = getDetailStatus(card);
  const statusConfig = STATUS_CONFIG[status];
  const now = new Date();

  // Timeline step completion 판정
  const stepsDone = [
    Boolean(card.issuedAt),
    Boolean(card.openedTime),
    card.extendCount > 0,
    Boolean(card.expiresAt) && now >= new Date(card.expiresAt),
    Boolean(card.extendDeadlineAt) && now >= new Date(card.extendDeadlineAt),
  ];

  // 마지막으로 완료된 스텝의 다음 = 현재 활성 스텝 (모두 완료면 -1)
  const lastDoneIdx = stepsDone.lastIndexOf(true);
  const activeStepIdx = lastDoneIdx + 1 >= stepsDone.length ? -1 : lastDoneIdx + 1;

  const stepsInfo: { label: string; date: string | null }[] = [
    { label: '발급',                          date: card.issuedAt },
    { label: '오픈',                          date: card.openedTime },
    { label: `연장 ${card.extendCount}회`,    date: null },
    { label: '만료',                          date: card.expiresAt },
    { label: '소멸',                          date: card.extendDeadlineAt },
  ];

  return (
    <Dialog
      open={true}
      onClose={onClose}
      maxWidth={false}
      PaperProps={{
        sx: {
          width: '100%',
          minWidth: 560,
          maxWidth: 720,
          maxHeight: '85vh',
          overflowY: 'auto',
        },
      }}
    >
      <DialogContent sx={{ p: 3 }}>

        {/* ── Header ── */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 3, flexWrap: 'wrap' }}>
          <Typography variant="h6" fontWeight={700}>
            Card #{card.cardId}
          </Typography>
          <Chip
            label={statusConfig.label}
            size="small"
            sx={{ bgcolor: statusConfig.bgcolor, color: statusConfig.color, fontWeight: 600 }}
          />
          <Box sx={{ flex: 1 }} />
          <Button
            size="small"
            disabled={status === 'DEAD'}
            onClick={() => onForceExpire(card.cardId)}
            sx={{
              bgcolor: '#FEE2E2',
              color: '#991B1B',
              fontSize: 12,
              px: 1.5,
              minWidth: 'auto',
              '&:hover': { bgcolor: '#FECACA' },
              '&.Mui-disabled': { bgcolor: '#F9FAFB', color: '#9CA3AF' },
            }}
          >
            강제 만료
          </Button>
          <Typography variant="body2" color="#6B7280" sx={{ minWidth: 'max-content' }}>
            발급: {formatDT(card.issuedAt)}
          </Typography>
        </Box>

        <Divider sx={{ mb: 3, borderColor: '#E5E7EB' }} />

        {/* ── 카드 당사자 ── */}
        <SectionHeader>카드 당사자</SectionHeader>
        <Box sx={{ display: 'flex', alignItems: 'stretch', gap: 1.5, mb: 3 }}>

          {/* 수신자 */}
          <Box
            sx={{
              flex: 1,
              p: 2,
              bgcolor: '#F9FAFB',
              border: '1px solid #E5E7EB',
              borderLeft: '3px solid #6366F1',
              borderRadius: 2,
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <Avatar sx={{ width: 36, height: 36, bgcolor: '#6366F1', fontSize: 14, fontWeight: 700 }}>
                {getInitials(card.recipient.name)}
              </Avatar>
              <Box>
                <Typography variant="body1" fontWeight={700}>{card.recipient.name}</Typography>
                <Typography variant="caption" color="#6B7280">ID: {card.recipient.userId}</Typography>
              </Box>
            </Box>
            <Typography variant="body2" color="#6B7280">
              나이: {card.recipient.age != null ? card.recipient.age : '정보 없음'}
            </Typography>
            <Typography variant="body2" color="#6B7280">
              지역: {card.recipient.region ?? '정보 없음'}
            </Typography>
            <Box sx={{ mt: 1.5 }}>
              <Chip
                label="수신자"
                size="small"
                sx={{ bgcolor: '#EEF2FF', color: '#4F46E5', fontWeight: 600, fontSize: 11 }}
              />
            </Box>
          </Box>

          {/* 화살표 */}
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', px: 0.5 }}>
            <Typography sx={{ fontSize: 22, color: '#9CA3AF' }}>→</Typography>
          </Box>

          {/* 발신자 */}
          <Box
            sx={{
              flex: 1,
              p: 2,
              bgcolor: '#F9FAFB',
              border: '1px solid #E5E7EB',
              borderLeft: '3px solid #EC4899',
              borderRadius: 2,
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <Avatar sx={{ width: 36, height: 36, bgcolor: '#EC4899', fontSize: 14, fontWeight: 700 }}>
                {getInitials(card.target.name)}
              </Avatar>
              <Box>
                <Typography variant="body1" fontWeight={700}>{card.target.name}</Typography>
                <Typography variant="caption" color="#6B7280">ID: {card.target.userId}</Typography>
              </Box>
            </Box>
            <Typography variant="body2" color="#6B7280">
              나이: {card.target.age != null ? card.target.age : '정보 없음'}
            </Typography>
            <Typography variant="body2" color="#6B7280">
              지역: {card.target.region ?? '정보 없음'}
            </Typography>
            <Box sx={{ mt: 1.5 }}>
              <Chip
                label="발신자"
                size="small"
                sx={{ bgcolor: '#FDF2F8', color: '#BE185D', fontWeight: 600, fontSize: 11 }}
              />
            </Box>
          </Box>
        </Box>

        <Divider sx={{ mb: 3, borderColor: '#E5E7EB' }} />

        {/* ── 타임라인 ── */}
        <SectionHeader>타임라인</SectionHeader>
        <Stepper
          nonLinear
          alternativeLabel
          activeStep={activeStepIdx}
          connector={DashedConnector}
          sx={{ mb: 3 }}
        >
          {stepsInfo.map((step, idx) => {
            const isActive = idx === activeStepIdx;
            return (
              <Step key={step.label} completed={stepsDone[idx]}>
                <StepLabel
                  StepIconComponent={CustomStepIcon}
                  optional={
                    stepsDone[idx] && step.date ? (
                      <Typography
                        variant="caption"
                        color="#6B7280"
                        sx={{ display: 'block', textAlign: 'center', mt: 0.5 }}
                      >
                        {formatDT(step.date)}
                      </Typography>
                    ) : null
                  }
                  sx={{
                    '& .MuiStepLabel-label': {
                      fontSize: 12,
                      fontWeight: stepsDone[idx] || isActive ? 600 : 400,
                      color: isActive ? '#F59E0B' : stepsDone[idx] ? '#10B981' : '#9CA3AF',
                      mt: '4px !important',
                    },
                  }}
                >
                  {step.label}
                </StepLabel>
              </Step>
            );
          })}
        </Stepper>

        <Divider sx={{ mb: 3, borderColor: '#E5E7EB' }} />

        {/* ── 하트 트랜잭션 ── */}
        <SectionHeader>하트 트랜잭션</SectionHeader>
        {card.heartTransactions.length === 0 ? (
          <Typography variant="body2" color="#9CA3AF" sx={{ textAlign: 'center', py: 2 }}>
            (트랜잭션 없음)
          </Typography>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
            {card.heartTransactions.map((tx, idx) => {
              const eventInfo = EVENT_CODE_MAP[tx.transactionType] ?? { label: tx.transactionType, icon: '•' };
              const isNegative = tx.amount < 0;
              return (
                <Box
                  key={idx}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.5,
                    px: 2,
                    py: 1,
                    bgcolor: idx % 2 === 0 ? '#F9FAFB' : '#FFFFFF',
                    border: '1px solid #E5E7EB',
                    borderRadius: 1,
                  }}
                >
                  <Typography sx={{ fontSize: 16, minWidth: 28, textAlign: 'center' }}>
                    {eventInfo.icon}
                  </Typography>
                  <Typography
                    variant="body2"
                    fontWeight={600}
                    sx={{ color: isNegative ? '#DC2626' : '#059669', minWidth: 52 }}
                  >
                    {tx.amount > 0 ? '+' : ''}{tx.amount}
                  </Typography>
                  <Typography variant="body2" sx={{ flex: 1 }}>
                    {eventInfo.label}
                  </Typography>
                  <Typography variant="caption" color="#9CA3AF">
                    {formatDT(tx.createdTime)}
                  </Typography>
                </Box>
              );
            })}
          </Box>
        )}

      </DialogContent>
    </Dialog>
  );
};

export default CupidCardDetailModal;
